import { useEffect, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Badge,
  Divider,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  CloudSyncOutlined,
  CloudOutlined,
  DatabaseOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  getDealBookings,
  getOpenAirUsers,
  getBookingTypes,
  previewOpenAirBookings,
  saveOpenAirBookings,
  deleteOpenAirBooking,
} from "../../services/Apis";
import dayjs from "dayjs";

interface OpenAirUser {
  id: string;
  name: string;
}

interface BookingType {
  id: string;
  name: string;
}

interface Booking {
  id?: number;
  resource_id?: number;
  key_id?: number;
  deal_id?: number;
  version_id?: number;
  ns_project_id?: string;
  user_id: string;
  user_name: string;
  booking_id?: string;
  allocation_percentage: number;
  start_date: string;
  end_date: string;
  notes?: string;
  booking_status?: string;
  sync_status?: string;
  booking_type_id?: string;
  booking_type_name?: string;
  approval_status?: string;
  region_code?: string;
  function_code?: string;
  career_level_name?: string;
  isNew?: boolean;
  isEditing?: boolean;
  _tempId?: string;
}

const SYNC_STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string; description: string }> = {
  synced: {
    color: "success",
    icon: <CheckCircleOutlined />,
    label: "Synced",
    description: "Exists in both database and OpenAir",
  },
  pending: {
    color: "warning",
    icon: <ClockCircleOutlined />,
    label: "Pending",
    description: "Saved locally, not yet synced to OpenAir",
  },
  failed: {
    color: "error",
    icon: <CloseCircleOutlined />,
    label: "Failed",
    description: "Sync to OpenAir failed — will retry on next save",
  },
  db_only: {
    color: "orange",
    icon: <DatabaseOutlined />,
    label: "DB Only",
    description: "In database but missing from OpenAir (may have been deleted externally)",
  },
  openair_only: {
    color: "purple",
    icon: <CloudOutlined />,
    label: "OpenAir Only",
    description: "Exists in OpenAir but not tracked in our database",
  },
};

export default function OpenAirBookings({
  dealData,
}: {
  dealData: any;
}) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [oaUsers, setOaUsers] = useState<OpenAirUser[]>([]);
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingKey, setEditingKey] = useState<string | number | null>(null);
  const [editingRecord, setEditingRecord] = useState<Booking | null>(null);
  const [hasModifications, setHasModifications] = useState(false);

  const hasNsProject = !!dealData?.ns_project_id;
  const hasUnsavedChanges = hasModifications || bookings.some((b) => b.isNew);

  useEffect(() => {
    setBookings([]);
    setHasModifications(false);
    setEditingKey(null);
    setEditingRecord(null);
    if (dealData?.deal_id && hasNsProject) {
      loadBookings();
      loadOaUsers();
      loadBookingTypes();
    }
  }, [dealData?.deal_id, hasNsProject]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await getDealBookings(dealData.deal_id, dealData.ns_project_id);
      if (res?.error) {
        setBookings([]);
      } else {
        setBookings(Array.isArray(res) ? res : []);
      }
    } catch {
      setBookings([]);
    }
    setLoading(false);
  };

  const loadOaUsers = async () => {
    try {
      const res = await getOpenAirUsers();
      if (res?.error) return;
      setOaUsers(Array.isArray(res) ? res : []);
    } catch { /* silently fail */ }
  };

  const loadBookingTypes = async () => {
    try {
      const res = await getBookingTypes();
      if (res?.error) return;
      setBookingTypes(Array.isArray(res) ? res : []);
    } catch { /* silently fail */ }
  };

  const handleCreateBookings = async () => {
    if (!hasNsProject) return;
    setGenerating(true);
    try {
      const res = await previewOpenAirBookings(dealData.deal_id, dealData.version_id);
      if (res?.error) {
        message.error(res.error);
        setGenerating(false);
        return;
      }
      if (Array.isArray(res) && res.length > 0) {
        const previewRows: Booking[] = res.map((b: any, idx: number) => ({
          ...b,
          booking_type_id: b.booking_type_id || "4",
          isNew: true,
          isEditing: false,
          _tempId: `new_${Date.now()}_${idx}`,
        }));
        setBookings((prev) => [...prev, ...previewRows]);
        message.success(`Generated ${res.length} booking(s) from resources`);
      } else {
        message.info("No resources found to generate bookings");
      }
    } catch {
      message.error("Failed to generate bookings");
    }
    setGenerating(false);
  };

  const handleAddRow = () => {
    const newRow: Booking = {
      resource_id: 0,
      user_id: "",
      user_name: "",
      allocation_percentage: 100,
      start_date: "",
      end_date: "",
      notes: "",
      booking_type_id: "9",
      isNew: true,
      isEditing: true,
      _tempId: `new_${Date.now()}`,
    };
    setBookings((prev) => [...prev, newRow]);
    setEditingKey(newRow._tempId!);
    setEditingRecord(newRow);
  };

  const handleEdit = (record: Booking) => {
    const key = record.id || record._tempId || record.booking_id;
    setEditingKey(key!);
    setEditingRecord({ ...record });
  };

  const handleCancelEdit = (record: Booking) => {
    if (record.isNew && !record.id) {
      if (!record.user_id && !record.start_date) {
        setBookings((prev) =>
          prev.filter((b) => (b._tempId || b.id) !== (record._tempId || record.id))
        );
      }
    }
    setEditingKey(null);
    setEditingRecord(null);
  };

  const handleSaveEdit = () => {
    if (!editingRecord) return;
    if ((editingRecord.id || editingRecord.sync_status === "openair_only") && !editingRecord.isNew) {
      setHasModifications(true);
    }
    setBookings((prev) =>
      prev.map((b) => {
        const bKey = b.id || b._tempId || b.booking_id;
        const eKey = editingRecord.id || editingRecord._tempId || editingRecord.booking_id;
        if (bKey === eKey) {
          return { ...editingRecord, isEditing: false };
        }
        return b;
      })
    );
    setEditingKey(null);
    setEditingRecord(null);
  };

  const handleDelete = async (record: Booking) => {
    if (record.id && !record.isNew) {
      const res = await deleteOpenAirBooking(record.id);
      if (res?.error) {
        message.error(res.error);
        return;
      }
      message.success("Booking deleted");
    } else if (record.sync_status === "openair_only" && record.booking_id) {
      const res = await deleteOpenAirBooking(undefined, record.booking_id);
      if (res?.error) {
        message.error(res.error);
        return;
      }
      message.success("Booking deleted from OpenAir");
    }
    setBookings((prev) =>
      prev.filter((b) => {
        const bKey = b.id || b._tempId || b.booking_id;
        const rKey = record.id || record._tempId || record.booking_id;
        return bKey !== rKey;
      })
    );
  };

  const handleSaveAll = async () => {
    const bookingsToSave = bookings.filter(
      (b) => b.isNew || b.id || (b.sync_status === "openair_only" && b.booking_id)
    );

    if (bookingsToSave.length === 0) {
      message.info("No bookings to save");
      return;
    }

    for (const b of bookingsToSave) {
      if (!b.user_id || !b.start_date || !b.end_date || !b.allocation_percentage) {
        message.warning("Please fill all required fields (User, Dates, Allocation) for all bookings");
        return;
      }
      if (b.start_date > b.end_date) {
        message.warning(`Start date (${b.start_date}) must be before end date (${b.end_date})`);
        return;
      }
    }

    setSaving(true);
    const payload = {
      deal_id: dealData.deal_id,
      version_id: dealData.version_id,
      ns_project_id: dealData.ns_project_id,
      bookings: bookingsToSave.map((b) => ({
        id: b.isNew ? null : b.id || null,
        resource_id: b.resource_id,
        user_id: b.user_id,
        user_name: b.user_name,
        booking_id: b.booking_id || null,
        allocation_percentage: b.allocation_percentage,
        start_date: b.start_date,
        end_date: b.end_date,
        notes: b.notes || "",
        booking_type_id: b.booking_type_id || "9",
      })),
    };

    const res = await saveOpenAirBookings(payload);
    if (res?.error) {
      message.error(res.error);
    } else {
      message.success("Bookings saved and synced to OpenAir");
      setHasModifications(false);
      await loadBookings();
    }
    setSaving(false);
  };

  const getRowKey = (record: Booking) =>
    record.id?.toString() || record._tempId || record.booking_id || `${record.user_id}_${record.start_date}`;

  const isEditing = (record: Booking) => {
    const key = record.id || record._tempId || record.booking_id;
    return key === editingKey;
  };

  // Summary counts
  const syncedCount = bookings.filter((b) => b.sync_status === "synced").length;
  const pendingCount = bookings.filter((b) => b.sync_status === "pending" || b.isNew).length;
  const failedCount = bookings.filter((b) => b.sync_status === "failed").length;
  const oaOnlyCount = bookings.filter((b) => b.sync_status === "openair_only").length;
  const dbOnlyCount = bookings.filter((b) => b.sync_status === "db_only").length;

  const columns = [
    {
      title: "Resource",
      dataIndex: "resource_id",
      key: "resource_id",
      width: 90,
      render: (val: number, record: Booking) => {
        if (record.key_id) return <Tag>{`R-${record.key_id}`}</Tag>;
        if (val && val > 0) return <Tag>{`#${val}`}</Tag>;
        return <span style={{ color: "#bbb" }}>—</span>;
      },
    },
    {
      title: "OpenAir User",
      dataIndex: "user_name",
      key: "user_name",
      width: 260,
      render: (val: string, record: Booking) => {
        if (isEditing(record)) {
          return (
            <Select
              showSearch
              style={{ width: "100%" }}
              value={editingRecord?.user_id || undefined}
              placeholder="Select User"
              filterOption={(input, option: any) => {
                const optionText = (option?.children || "").toLowerCase();
                const searchText = input.toLowerCase().trim();
                if (!searchText) return true;
                if (optionText.includes(searchText)) return true;
                const optionNoSpaces = optionText.replace(/[\s,]+/g, "");
                const searchNoSpaces = searchText.replace(/[\s,]+/g, "");
                if (optionNoSpaces.includes(searchNoSpaces)) return true;
                const searchParts = searchText.split(/[\s,]+/).filter(Boolean);
                if (searchParts.length > 1) {
                  const optionNormalized = optionText.replace(/,/g, " ");
                  if (searchParts.every((part) => optionNormalized.includes(part))) return true;
                }
                return false;
              }}
              onChange={(value) => {
                const user = oaUsers.find((u) => u.id === value);
                setEditingRecord((prev) =>
                  prev ? { ...prev, user_id: value, user_name: user?.name || "" } : prev
                );
              }}
            >
              {oaUsers.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name}
                </Select.Option>
              ))}
            </Select>
          );
        }
        return val || <Tag color="warning">No user mapped</Tag>;
      },
    },
    {
      title: "Type",
      dataIndex: "booking_type_name",
      key: "booking_type_name",
      width: 150,
      render: (val: string, record: Booking) => {
        if (isEditing(record)) {
          return (
            <Select
              style={{ width: "100%" }}
              value={editingRecord?.booking_type_id || undefined}
              placeholder="Booking Type"
              onChange={(value) => {
                const bt = bookingTypes.find((t) => t.id === value);
                setEditingRecord((prev) =>
                  prev
                    ? { ...prev, booking_type_id: value, booking_type_name: bt?.name || "" }
                    : prev
                );
              }}
            >
              {bookingTypes.map((bt) => (
                <Select.Option key={bt.id} value={bt.id}>
                  {bt.name}
                </Select.Option>
              ))}
            </Select>
          );
        }
        return val || <span style={{ color: "#bbb" }}>—</span>;
      },
    },
    {
      title: "Allocation %",
      dataIndex: "allocation_percentage",
      key: "allocation_percentage",
      width: 110,
      render: (val: number, record: Booking) => {
        if (isEditing(record)) {
          return (
            <InputNumber
              min={1}
              max={100}
              value={editingRecord?.allocation_percentage}
              onChange={(value) =>
                setEditingRecord((prev) =>
                  prev ? { ...prev, allocation_percentage: value || 0 } : prev
                )
              }
              style={{ width: "100%" }}
              suffix="%"
            />
          );
        }
        return `${val}%`;
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      width: 140,
      render: (val: string, record: Booking) => {
        if (isEditing(record)) {
          return (
            <DatePicker
              value={editingRecord?.start_date ? dayjs(editingRecord.start_date) : null}
              disabledDate={(current) =>
                editingRecord?.end_date ? current.isAfter(dayjs(editingRecord.end_date), "day") : false
              }
              onChange={(date) =>
                setEditingRecord((prev) =>
                  prev ? { ...prev, start_date: date ? date.format("YYYY-MM-DD") : "" } : prev
                )
              }
              style={{ width: "100%" }}
            />
          );
        }
        return val || "—";
      },
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      width: 140,
      render: (val: string, record: Booking) => {
        if (isEditing(record)) {
          return (
            <DatePicker
              value={editingRecord?.end_date ? dayjs(editingRecord.end_date) : null}
              disabledDate={(current) =>
                editingRecord?.start_date ? current.isBefore(dayjs(editingRecord.start_date), "day") : false
              }
              onChange={(date) =>
                setEditingRecord((prev) =>
                  prev ? { ...prev, end_date: date ? date.format("YYYY-MM-DD") : "" } : prev
                )
              }
              style={{ width: "100%" }}
            />
          );
        }
        return val || "—";
      },
    },
    {
      title: "Status",
      dataIndex: "sync_status",
      key: "sync_status",
      width: 130,
      filters: [
        { text: "Synced", value: "synced" },
        { text: "Pending", value: "pending" },
        { text: "Failed", value: "failed" },
        { text: "DB Only", value: "db_only" },
        { text: "OpenAir Only", value: "openair_only" },
      ],
      onFilter: (value: any, record: Booking) => {
        if (record.isNew) return value === "pending";
        return record.sync_status === value;
      },
      render: (_: string, record: Booking) => {
        if (record.isNew) {
          return (
            <Tooltip title="Not yet saved — will sync on save">
              <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>
            </Tooltip>
          );
        }
        const status = record.sync_status || "pending";
        const config = SYNC_STATUS_CONFIG[status] || SYNC_STATUS_CONFIG.pending;
        return (
          <Tooltip title={config.description}>
            <Tag icon={config.icon} color={config.color}>
              {config.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 180,
      ellipsis: true,
      render: (val: string, record: Booking) => {
        if (isEditing(record)) {
          return (
            <Input
              value={editingRecord?.notes}
              onChange={(e) =>
                setEditingRecord((prev) =>
                  prev ? { ...prev, notes: e.target.value } : prev
                )
              }
              placeholder="Notes"
            />
          );
        }
        return val || <span style={{ color: "#bbb" }}>—</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      fixed: "right" as const,
      render: (_: any, record: Booking) => {
        if (isEditing(record)) {
          return (
            <Space size={4}>
              <Tooltip title="Confirm">
                <Button
                  type="link"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={handleSaveEdit}
                  style={{ color: "#52c41a" }}
                />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  type="link"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => handleCancelEdit(record)}
                  style={{ color: "#ff4d4f" }}
                />
              </Tooltip>
            </Space>
          );
        }
        return (
          <Space size={4}>
            <Tooltip title="Edit">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                style={{ color: "#6a48f3" }}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this booking?"
              description={record.booking_id ? "This will remove it from OpenAir." : undefined}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button type="link" size="small" icon={<DeleteOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CloudSyncOutlined style={{ fontSize: 18, color: "#6a48f3" }} />
          <span style={{ fontWeight: 600 }}>OpenAir Bookings</span>
          {!hasNsProject && (
            <Tooltip title="This deal is not linked to an OpenAir project. NS Project ID is required.">
              <Tag icon={<WarningOutlined />} color="warning">
                Not an OpenAir Project
              </Tag>
            </Tooltip>
          )}
        </div>
      }
      extra={
        <Space size={8} wrap>
          <Tooltip title="Refresh from OpenAir">
            <Button
              icon={<ReloadOutlined />}
              onClick={loadBookings}
              loading={loading}
              disabled={!hasNsProject}
              size="middle"
            />
          </Tooltip>
          <Tooltip title={!hasNsProject ? "Not an OpenAir project" : "Add a blank booking row"}>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddRow}
              disabled={!hasNsProject}
              size="middle"
            >
              Add Row
            </Button>
          </Tooltip>
          <Tooltip
            title={
              !hasNsProject
                ? "NS Project ID missing"
                : "Auto-generate bookings from deal resources"
            }
          >
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleCreateBookings}
              disabled={!hasNsProject}
              loading={generating}
              style={hasNsProject ? { color: "#6a48f3", borderColor: "#6a48f3" } : {}}
              size="middle"
            >
              Create from Resources
            </Button>
          </Tooltip>
          {hasUnsavedChanges && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveAll}
              loading={saving}
              disabled={!hasNsProject}
              style={{ backgroundColor: "#6a48f3", borderColor: "#6a48f3" }}
              size="middle"
            >
              Save & Sync to OpenAir
            </Button>
          )}
        </Space>
      }
      styles={{ body: { padding: "12px 16px" } }}
    >
      {/* Summary badges */}
      {bookings.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <Badge count={bookings.length} showZero color="#6a48f3" overflowCount={999}>
            <Tag style={{ padding: "2px 8px" }}>Total</Tag>
          </Badge>
          {syncedCount > 0 && (
            <Badge count={syncedCount} color="#52c41a">
              <Tag style={{ padding: "2px 8px" }}>Synced</Tag>
            </Badge>
          )}
          {pendingCount > 0 && (
            <Badge count={pendingCount} color="#faad14">
              <Tag style={{ padding: "2px 8px" }}>Pending</Tag>
            </Badge>
          )}
          {failedCount > 0 && (
            <Badge count={failedCount} color="#ff4d4f">
              <Tag style={{ padding: "2px 8px" }}>Failed</Tag>
            </Badge>
          )}
          {oaOnlyCount > 0 && (
            <Badge count={oaOnlyCount} color="#722ed1">
              <Tag style={{ padding: "2px 8px" }}>OpenAir Only</Tag>
            </Badge>
          )}
          {dbOnlyCount > 0 && (
            <Badge count={dbOnlyCount} color="#fa8c16">
              <Tag style={{ padding: "2px 8px" }}>DB Only</Tag>
            </Badge>
          )}
        </div>
      )}

      <Table
        dataSource={bookings}
        columns={columns}
        rowKey={getRowKey}
        loading={loading}
        scroll={{ x: 1400 }}
        size="small"
        pagination={bookings.length > 15 ? { pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} bookings` } : false}
        rowClassName={(record) => {
          if (record.isNew) return "booking-row-new";
          if (record.sync_status === "openair_only") return "booking-row-oa-only";
          if (record.sync_status === "failed") return "booking-row-failed";
          return "";
        }}
        locale={{
          emptyText: hasNsProject
            ? "No bookings found. Click 'Create from Resources' to auto-generate or 'Add Row' to create manually."
            : "Not an OpenAir project — NS Project ID is required to manage bookings.",
        }}
      />

      {/* Status legend */}
      {bookings.length > 0 && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#666" }}>
            {Object.entries(SYNC_STATUS_CONFIG).map(([key, cfg]) => (
              <span key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Tag icon={cfg.icon} color={cfg.color} style={{ fontSize: 11, margin: 0 }}>
                  {cfg.label}
                </Tag>
                <span>{cfg.description}</span>
              </span>
            ))}
          </div>
        </>
      )}

      <style>{`
        .booking-row-new { background-color: #e6f4ff !important; }
        .booking-row-oa-only { background-color: #f9f0ff !important; }
        .booking-row-failed { background-color: #fff2f0 !important; }
        .ant-table-row:hover .ant-table-cell-fix-right { background: #fafafa !important; }
        .booking-row-new:hover .ant-table-cell-fix-right { background: #e6f4ff !important; }
        .booking-row-oa-only:hover .ant-table-cell-fix-right { background: #f9f0ff !important; }
        .booking-row-failed:hover .ant-table-cell-fix-right { background: #fff2f0 !important; }
      `}</style>
    </Card>
  );
}
